import {
  Component,
  effect,
  EventEmitter,
  Input,
  input,
  InputSignal,
  Output,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { SpinnerComponent } from '../../../spinner/spinner.component';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexFill,
  ApexTooltip,
  ApexXAxis,
  ApexLegend,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexYAxis,
} from 'ng-apexcharts';
export type ChartOptionsMultiple = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  markers: ApexMarkers; //ApexMarkers;
  stroke: any; //ApexStroke;
  yaxis: ApexYAxis | ApexYAxis[];
  dataLabels: ApexDataLabels;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  fill: ApexFill;
  tooltip: ApexTooltip;
};
import { NgApexchartsModule } from 'ng-apexcharts';
import {TranslatePipe} from "@ngx-translate/core";
import {
  ICD10ChartItem,
  MostFrequentAdmissionICD10ChartFiltersDto,
} from '../../../../interfaces/reports/most-frequent-admission-ICD10';
import { ChiSquaredResult, PDropdownOptions } from '../../../../interfaces';
import { FilterItem } from '../../../../interfaces/reports/filter-Interface';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { NgClass, TitleCasePipe } from '@angular/common';
import { extend } from '@svgdotjs/svg.js';
import { BaseDashboardChartComponent } from '../../base-dashboard-chart.component';
import { ChartCardComponent } from '../../chart-card/chart-card.component';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
type FilterModel = 'All' | string | string[] | number[] | number;

@Component({
  selector: 'co-multiple-y-axis-chart',
  standalone: true,
  imports: [NgApexchartsModule, ChartCardComponent, FormsModule, SelectModule, SpinnerComponent, TranslatePipe],
  templateUrl: './multiple-y-axis-chart.component.html',
  styleUrl: './multiple-y-axis-chart.component.css',
})
export class MultipleYAxisChartComponent extends BaseDashboardChartComponent {
  private langChangeSub?: Subscription;

  chartData: InputSignal<any[]> = input.required();
  chiSquaredResult = input<ChiSquaredResult | null>();
  @Input() chiSquared?: boolean = true;

  @Input() xAxisLabel?: string;
  @Input() chartMiniHeader?: string;
  @Input() cumulativeLabel?: string;
  @Input() tooltipBuilder?: (item: any, seriesIndex: number, totalSeries: number) => string;
  chartReady = input.required<boolean>();


  @ViewChild('chart') chart?: ChartComponent;
  public chartOptions: Partial<ChartOptionsMultiple> | any;

  constructor(private translate: TranslateService) {
    super();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.updateChartOptions();
    });
    effect(() => {
      if (this.chartData())
        this.updateChartOptions();
    })
  }
  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  ngOnInit() {
    this.updateChartOptions();
  }

  override onDropdownChange() {
    this.onFilterChange.emit(this.selectedFilters);
  }

  private updateChartOptions() {
    const data = this.chartData();
    if (!data || data.length === 0) return;

    const isTopDoctors = 'doctorName' in data[0];
    const isICD10 = 'icd10Code' in data[0];
    const isMDC = 'mdcCode' in data[0];

    const currentData = data.map((item) =>
      isTopDoctors ? item.claimCount : item.currentPercent
    );
    const cumulativeData = data.map((item) =>
      isTopDoctors ? item.cumulativePercent : item.cumulativeCurrentPercent
    );

    const historicalData = !isTopDoctors ? data.map(item => item.historicalPercent) : [];
    const includeHistorical = historicalData.some(val => val > 0);

    const series = [
      { name: isTopDoctors ? 'Claims' : 'ICD10', type: 'column', data: currentData, color: 'steelblue' },
      includeHistorical && { name: 'Percentage', type: 'column', data: historicalData, color: 'lightgray' },
      { name: 'Cumulative', type: 'line', data: cumulativeData, color: '#DC143C' },
    ].filter(Boolean);

    this.chartOptions = {
      series,
      chart: {
        height: 350,
        type: 'line',
        stacked: false,
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true },
        },
      },
      colors: ['steelblue', 'lightgray', '#DC143C'],
      plotOptions: { bar: { columnWidth: '100%' } },
      dataLabels: { enabled: false },
      stroke: { width: [1, 1, 4] },
      markers: { size: 6, hover: { size: 10 } },
      title: { text: this.chartMiniHeader, align: 'left' },
      xaxis: {
        categories: data.map(item => {
          if (isTopDoctors) return this.wrapTopDoctorLabel(item.doctorName);
          if (isICD10) return item.icd10Code;
          if (isMDC) return item.mdcCode;
          return '';
        }),
        title: {
          text: this.translate.instant(this.xAxisLabel || ''),
          align: 'center',
        },
        labels: {
          rotate: isTopDoctors ? -45 : 0,
          rotateAlways: isTopDoctors,
          maxHeight: isTopDoctors ? 120 : undefined,
          style: {
            fontSize: isTopDoctors ? '10px' : '12px',
          }, 
          trim: false,
        },
        tooltip: { enabled: false },
      },
      yaxis: [
        {
          axisTicks: { show: true },
          axisBorder: { show: true, color: 'steelblue' },
          labels: { style: { color: 'steelblue' } },
          title: {
            text: this.translate.instant(
              isTopDoctors ? 'CHART.CLAIMS' : 'CHART.PERCENTAGE_OF_CLAIMS'
            ),
            style: { color: 'steelblue' },
          },
          tooltip: { enabled: false },
        },
        includeHistorical && {
          seriesName: 'Percentage',
          opposite: true,
          axisTicks: { show: true },
          axisBorder: { show: true, color: 'lightgray' },
          labels: { style: { color: 'lightgray' } },
          title: {
            text: this.translate.instant('CHART.PERCENTAGE_OF_CLAIMS_HISTORICAL'),
            style: { color: 'lightgray' },
          },
        },
        {
          seriesName: 'Cumulative',
          opposite: true,
          axisTicks: { show: true },
          axisBorder: { show: true, color: '#FEB019' },
          labels: { style: { color: '#FEB019' } },
          title: {
            text: this.translate.instant(this.cumulativeLabel || ''),
            style: { color: '#DC143C' },
          },
        },
      ].filter(Boolean),
      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        y: {
          show: false,
        },
        custom: ({ seriesIndex, dataPointIndex }: { seriesIndex: number; dataPointIndex: number }) => {
          const item = this.chartData()[dataPointIndex];
          if (!item) return '';
            const totalSeries = this.chartOptions.series.length;
          return this.tooltipBuilder ?  this.tooltipBuilder(item, seriesIndex, totalSeries) : '';
        },
      },
      legend: { horizontalAlign: 'left', offsetX: 40 },
    };
  }

  private wrapTopDoctorLabel(label: string): string | string[] {
    const trimmedLabel = label?.trim() ?? '';
    const hyphenIndex = trimmedLabel.indexOf('-');

    if (hyphenIndex > 0 && hyphenIndex < trimmedLabel.length - 1) {
      const beforeHyphen = trimmedLabel.slice(0, hyphenIndex).trim();
      const afterHyphen = trimmedLabel.slice(hyphenIndex + 1).trim();

      return [`${beforeHyphen} -`, afterHyphen];
    }

    const words = trimmedLabel.split(/\s+/).filter(Boolean);
    if (words.length < 2) return trimmedLabel;

    const splitIndex = Math.ceil(words.length / 2);
    return [
      words.slice(0, splitIndex).join(' '),
      words.slice(splitIndex).join(' ')
    ];
  }
}
